import Seo from "@/components/Seo";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import {
  ArrowUpRight,
  Compass,
  HardHat,
  TrafficCone,
  Cpu,
  Handshake,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { SECTOR_NETWORK } from "@/data/industries";
import { CLIENTS } from "@/data/clients";

// Lookup for sector icons - kept here so data can stay serialisable.
const SECTOR_ICONS = {
  Compass,
  HardHat,
  TrafficCone,
  Cpu,
  Handshake,
};
const SectorIcon = ({ name, ...props }) => {
  const Icon = SECTOR_ICONS[name] || Compass;
  return <Icon {...props} />;
};

// ─────────────────────────────────────────────────────────────
// Motion presets (Apple-style easing: 120-350ms, no bounce)
// ─────────────────────────────────────────────────────────────
const EASE = [0.16, 1, 0.3, 1];
const glide = (delay = 0, duration = 0.35) => ({
  initial: { opacity: 0, y: 12 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-40px" },
  transition: { duration, delay, ease: EASE },
});

// ─────────────────────────────────────────────────────────────
// Animated counter - glides from 0 to target when in view.
// ─────────────────────────────────────────────────────────────
function Counter({ end, prefix = "", suffix = "", format = false }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [n, setN] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    const duration = 1400;
    let raf = 0;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      // easeOutExpo
      const eased = p === 1 ? 1 : 1 - Math.pow(2, -10 * p);
      setN(Math.floor(eased * end));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, end]);

  const display = format ? n.toLocaleString("en-GB") : n;
  return (
    <span ref={ref}>
      {prefix}
      {display}
      {suffix}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────
// Blueprint / topography background (subtle, engineered)
// ─────────────────────────────────────────────────────────────
function BlueprintBg({ opacity = 0.08 }) {
  return (
    <div
      aria-hidden
      className="absolute inset-0 pointer-events-none"
      style={{
        opacity,
        backgroundImage: `
          linear-gradient(rgba(148,163,184,0.4) 1px, transparent 1px),
          linear-gradient(90deg, rgba(148,163,184,0.4) 1px, transparent 1px),
          radial-gradient(circle at 25% 40%, rgba(56,189,248,0.15) 0%, transparent 55%),
          radial-gradient(circle at 75% 65%, rgba(139,92,246,0.10) 0%, transparent 55%)
        `,
        backgroundSize: "56px 56px, 56px 56px, 100% 100%, 100% 100%",
      }}
    />
  );
}

// ─────────────────────────────────────────────────────────────
// Network hub - desktop pentagon; mobile compact grid with connectors.
// ─────────────────────────────────────────────────────────────
function NetworkHubDesktop() {
  const size = 760;
  const cx = size / 2;
  const cy = size / 2;
  const radius = 275;

  const nodes = SECTOR_NETWORK.map((s, i) => {
    const angle = (-90 + i * 72) * (Math.PI / 180);
    return {
      ...s,
      x: cx + radius * Math.cos(angle),
      y: cy + radius * Math.sin(angle),
    };
  });

  return (
    <div className="relative mx-auto w-full max-w-[760px] aspect-square hidden md:block">
      <svg
        viewBox={`0 0 ${size} ${size}`}
        className="w-full h-full"
        aria-hidden
      >
        <defs>
          <radialGradient id="hubGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#22D3EE" stopOpacity="0.35" />
            <stop offset="60%" stopColor="#22D3EE" stopOpacity="0.06" />
            <stop offset="100%" stopColor="#22D3EE" stopOpacity="0" />
          </radialGradient>
        </defs>

        <circle cx={cx} cy={cy} r={200} fill="url(#hubGlow)" />

        {nodes.map((node, i) => (
          <g key={node.key}>
            <motion.line
              x1={cx}
              y1={cy}
              x2={node.x}
              y2={node.y}
              stroke={node.color}
              strokeOpacity={0.35}
              strokeWidth={1.2}
              initial={{ pathLength: 0, opacity: 0 }}
              whileInView={{ pathLength: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.15 + i * 0.08, ease: EASE }}
            />
            <circle r={3} fill={node.color}>
              <animateMotion
                dur={`${4 + i * 0.4}s`}
                repeatCount="indefinite"
                begin={`${i * 0.8}s`}
                path={`M ${cx} ${cy} L ${node.x} ${node.y}`}
              />
              <animate
                attributeName="opacity"
                values="0;1;1;0"
                dur={`${4 + i * 0.4}s`}
                repeatCount="indefinite"
                begin={`${i * 0.8}s`}
              />
            </circle>
          </g>
        ))}

        {[80, 130, 180].map((r) => (
          <circle
            key={r}
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke="#22D3EE"
            strokeOpacity={0.08}
            strokeDasharray="2 6"
          />
        ))}
      </svg>

      {/* Central hub tile */}
      <motion.div
        {...glide(0, 0.4)}
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center justify-center"
      >
        <div className="relative">
          <div className="absolute inset-0 rounded-2xl bg-cyan-400 blur-2xl opacity-30 animate-pulse" />
          <div className="relative rounded-2xl bg-slate-900/90 backdrop-blur border border-cyan-400/40 px-6 py-5 text-center shadow-2xl">
            <div className="text-[10px] font-mono uppercase tracking-widest text-cyan-300">
              The Hub
            </div>
            <div className="mt-2 font-heading font-black text-xl md:text-2xl text-white tracking-tight leading-tight">
              HighwaysIndustry
              <span className="text-cyan-300">.com</span>
            </div>
            <div className="mt-2 text-[10px] text-white/50">Since 2011</div>
          </div>
        </div>
      </motion.div>

      {nodes.map((node, i) => (
        <motion.a
          key={node.key}
          href={`#${node.key}`}
          {...glide(0.25 + i * 0.06, 0.4)}
          data-testid={`network-node-${node.key}`}
          className="absolute -translate-x-1/2 -translate-y-1/2 group"
          style={{
            left: `${(node.x / size) * 100}%`,
            top: `${(node.y / size) * 100}%`,
          }}
        >
          <div
            className="relative rounded-2xl bg-slate-900/85 backdrop-blur border border-white/10 group-hover:border-white/40 px-4 py-3 min-w-[180px] transition-all duration-300"
            style={{ boxShadow: `0 0 32px -12px ${node.color}` }}
          >
            <div className="flex items-center gap-2">
              <span
                className="inline-flex items-center justify-center h-8 w-8 rounded-full flex-shrink-0"
                style={{ background: `${node.color}22`, color: node.color }}
              >
                <SectorIcon name={node.iconName} size={16} strokeWidth={2} />
              </span>
              <div>
                <div className="font-heading font-bold text-white text-sm leading-tight">
                  {node.label}
                </div>
                <div className="text-[10px] text-white/50 mt-0.5">
                  {node.tagline}
                </div>
              </div>
            </div>
            <span
              className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full animate-ping"
              style={{ background: node.color }}
            />
            <span
              className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full"
              style={{ background: node.color }}
            />
          </div>
        </motion.a>
      ))}
    </div>
  );
}

function NetworkHubMobile() {
  // Vertical constellation: hub tile centred on top, 5 sector cards below
  // in a single column, each with an animated connector line back to the hub.
  return (
    <div className="md:hidden relative mx-auto max-w-md">
      {/* Hub tile */}
      <motion.div
        {...glide(0, 0.4)}
        className="relative mx-auto flex justify-center"
      >
        <div className="relative">
          <div className="absolute inset-0 rounded-2xl bg-cyan-400 blur-2xl opacity-30 animate-pulse" />
          <div className="relative rounded-2xl bg-slate-900/90 backdrop-blur border border-cyan-400/40 px-5 py-4 text-center shadow-2xl">
            <div className="text-[9px] font-mono uppercase tracking-widest text-cyan-300">
              The Hub
            </div>
            <div className="mt-1 font-heading font-black text-lg text-white tracking-tight leading-tight">
              HighwaysIndustry
              <span className="text-cyan-300">.com</span>
            </div>
            <div className="mt-1 text-[9px] text-white/50">Since 2011</div>
          </div>
        </div>
      </motion.div>

      {/* Vertical connector rail with travelling pulses */}
      <div className="relative mt-3">
        <svg
          viewBox="0 0 100 500"
          preserveAspectRatio="none"
          aria-hidden
          className="absolute left-0 top-0 w-full h-full"
        >
          <motion.line
            x1="50"
            y1="0"
            x2="50"
            y2="500"
            stroke="#22D3EE"
            strokeOpacity={0.3}
            strokeWidth={0.6}
            strokeDasharray="1 3"
            initial={{ pathLength: 0 }}
            whileInView={{ pathLength: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: EASE }}
          />
          {SECTOR_NETWORK.map((s, i) => (
            <circle key={s.key} r={1.6} fill={s.color}>
              <animate
                attributeName="cy"
                values="0;500"
                dur={`${5 + i * 0.5}s`}
                repeatCount="indefinite"
                begin={`${i * 0.9}s`}
              />
              <animate
                attributeName="cx"
                values="50;50"
                dur={`${5 + i * 0.5}s`}
                repeatCount="indefinite"
                begin={`${i * 0.9}s`}
              />
              <animate
                attributeName="opacity"
                values="0;1;1;0"
                dur={`${5 + i * 0.5}s`}
                repeatCount="indefinite"
                begin={`${i * 0.9}s`}
              />
            </circle>
          ))}
        </svg>

        <div className="relative flex flex-col gap-3 py-4">
          {SECTOR_NETWORK.map((node, i) => (
            <motion.a
              key={node.key}
              href={`#${node.key}`}
              {...glide(0.15 + i * 0.05, 0.35)}
              data-testid={`network-node-${node.key}`}
              className="relative mx-4 rounded-2xl bg-slate-900/85 backdrop-blur border border-white/10 px-4 py-3"
              style={{ boxShadow: `0 0 24px -14px ${node.color}` }}
            >
              <div className="flex items-center gap-3">
                <span
                  className="inline-flex items-center justify-center h-9 w-9 rounded-full flex-shrink-0"
                  style={{ background: `${node.color}22`, color: node.color }}
                >
                  <SectorIcon name={node.iconName} size={18} strokeWidth={2} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-heading font-bold text-white text-sm leading-tight">
                    {node.label}
                  </div>
                  <div className="text-[10px] text-white/50 mt-0.5">
                    {node.tagline}
                  </div>
                </div>
                <ArrowUpRight
                  size={14}
                  className="text-white/40 flex-shrink-0"
                />
              </div>
              <span
                className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full animate-ping"
                style={{ background: node.color }}
              />
              <span
                className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full"
                style={{ background: node.color }}
              />
            </motion.a>
          ))}
        </div>
      </div>
    </div>
  );
}

function NetworkHub() {
  return (
    <>
      <NetworkHubDesktop />
      <NetworkHubMobile />
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// Decorative animated triangulated mesh overlaid on each sector
// image. Curated triangle graph with staged line-draw, pulsing
// nodes, breathing triangle fills and data pulses travelling
// along random edges.
// ─────────────────────────────────────────────────────────────
const MESH_NODES = [
  { x: 12, y: 14 },
  { x: 40, y: 8 },
  { x: 68, y: 12 },
  { x: 90, y: 22 },
  { x: 24, y: 30 },
  { x: 54, y: 28 },
  { x: 80, y: 38 },
  { x: 10, y: 48 },
  { x: 38, y: 50 },
  { x: 62, y: 48 },
  { x: 88, y: 56 },
  { x: 22, y: 68 },
  { x: 48, y: 72 },
  { x: 74, y: 70 },
  { x: 14, y: 88 },
  { x: 40, y: 90 },
  { x: 66, y: 92 },
  { x: 90, y: 84 },
];

// Curated Delaunay-esque triangles (indices into MESH_NODES).
const MESH_TRIANGLES = [
  [0, 1, 4],
  [1, 4, 5],
  [1, 2, 5],
  [2, 5, 6],
  [2, 3, 6],
  [3, 6, 10],
  [4, 5, 8],
  [5, 8, 9],
  [5, 6, 9],
  [6, 9, 10],
  [4, 7, 8],
  [7, 8, 11],
  [8, 9, 12],
  [8, 11, 12],
  [9, 12, 13],
  [9, 10, 13],
  [10, 13, 17],
  [7, 11, 14],
  [11, 12, 15],
  [11, 14, 15],
  [12, 15, 16],
  [12, 13, 16],
  [13, 16, 17],
];

// Extract unique edges from the triangle list.
const MESH_EDGES = (() => {
  const key = (a, b) => (a < b ? `${a}-${b}` : `${b}-${a}`);
  const seen = new Set();
  const edges = [];
  MESH_TRIANGLES.forEach(([a, b, c]) => {
    [[a, b], [b, c], [a, c]].forEach(([p, q]) => {
      const k = key(p, q);
      if (!seen.has(k)) {
        seen.add(k);
        edges.push([p, q]);
      }
    });
  });
  return edges;
})();

function NetworkOverlay({ color }) {
  return (
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      aria-hidden
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ mixBlendMode: "screen" }}
    >
      {/* Triangle fills breathing softly */}
      {MESH_TRIANGLES.map(([a, b, c], i) => {
        const na = MESH_NODES[a];
        const nb = MESH_NODES[b];
        const nc = MESH_NODES[c];
        const duration = 3.6 + (i % 5) * 0.5;
        const delay = (i * 0.22) % duration;
        return (
          <polygon
            key={`t-${i}`}
            points={`${na.x},${na.y} ${nb.x},${nb.y} ${nc.x},${nc.y}`}
            fill={color}
            fillOpacity={0}
          >
            <animate
              attributeName="fill-opacity"
              values="0;0.09;0"
              dur={`${duration}s`}
              begin={`${delay}s`}
              repeatCount="indefinite"
            />
          </polygon>
        );
      })}

      {/* Triangle edges - staged draw-in */}
      {MESH_EDGES.map(([a, b], i) => {
        const na = MESH_NODES[a];
        const nb = MESH_NODES[b];
        return (
          <motion.line
            key={`e-${i}`}
            x1={na.x}
            y1={na.y}
            x2={nb.x}
            y2={nb.y}
            stroke={color}
            strokeOpacity={0.55}
            strokeWidth={0.28}
            initial={{ pathLength: 0 }}
            whileInView={{ pathLength: 1 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{
              duration: 0.7,
              delay: 0.08 + (i * 0.02) % 0.6,
              ease: EASE,
            }}
          />
        );
      })}

      {/* Data pulses travelling along a curated subset of edges */}
      {[0, 3, 6, 10, 14, 18, 22, 26, 30].map((edgeIdx, i) => {
        const edge = MESH_EDGES[edgeIdx % MESH_EDGES.length];
        if (!edge) return null;
        const na = MESH_NODES[edge[0]];
        const nb = MESH_NODES[edge[1]];
        const dur = 2 + (i % 4) * 0.5;
        return (
          <circle key={`p-${i}`} r={0.7} fill={color}>
            <animateMotion
              dur={`${dur}s`}
              repeatCount="indefinite"
              begin={`${i * 0.3}s`}
              path={`M ${na.x} ${na.y} L ${nb.x} ${nb.y}`}
            />
            <animate
              attributeName="opacity"
              values="0;1;1;0"
              dur={`${dur}s`}
              begin={`${i * 0.3}s`}
              repeatCount="indefinite"
            />
          </circle>
        );
      })}

      {/* Nodes - small solid dot + expanding halo */}
      {MESH_NODES.map((n, i) => {
        const dur = 2.4 + (i % 6) * 0.25;
        const delay = (i * 0.18) % dur;
        return (
          <g key={`n-${i}`}>
            <circle cx={n.x} cy={n.y} r={0.55} fill={color} opacity={0.95} />
            <circle cx={n.x} cy={n.y} r={1.6} fill="none" stroke={color}>
              <animate
                attributeName="r"
                values="0.6;3.2;0.6"
                dur={`${dur}s`}
                begin={`${delay}s`}
                repeatCount="indefinite"
              />
              <animate
                attributeName="stroke-opacity"
                values="0.5;0;0.5"
                dur={`${dur}s`}
                begin={`${delay}s`}
                repeatCount="indefinite"
              />
              <animate
                attributeName="stroke-width"
                values="0.35;0.05;0.35"
                dur={`${dur}s`}
                begin={`${delay}s`}
                repeatCount="indefinite"
              />
            </circle>
          </g>
        );
      })}
    </svg>
  );
}


// ─────────────────────────────────────────────────────────────
// Full-bleed sector card (headline, body, chip grid)
// ─────────────────────────────────────────────────────────────
function SectorCard({ sector, index }) {
  const reverse = index % 2 === 1;
  return (
    <section
      id={sector.key}
      data-testid={`sector-card-${sector.key}`}
      className="relative py-14 sm:py-20 md:py-32 scroll-mt-24 overflow-hidden"
    >
      <BlueprintBg opacity={0.05} />
      <div className="relative mx-auto max-w-[1400px] px-5 md:px-10">
        <div
          className={`grid lg:grid-cols-12 gap-8 md:gap-10 items-center ${
            reverse ? "lg:[&>*:first-child]:order-2" : ""
          }`}
        >
          {/* Visual */}
          <motion.div
            {...glide(0, 0.4)}
            className="lg:col-span-6 relative rounded-3xl overflow-hidden aspect-[4/3] bg-slate-900"
          >
            <img
              src={sector.image}
              alt={sector.label}
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
            />
            <div
              aria-hidden
              className={`absolute inset-0 bg-gradient-to-tr ${sector.gradient}`}
            />
            <div
              aria-hidden
              className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-transparent"
            />
            {/* Connected network animation overlay */}
            <NetworkOverlay color={sector.color} />
            {/* Sector label badge */}
            <div
              className="absolute bottom-5 left-5 inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-heading font-semibold backdrop-blur"
              style={{
                background: `${sector.color}18`,
                color: sector.color,
                border: `1px solid ${sector.color}55`,
              }}
            >
              <SectorIcon
                name={sector.iconName}
                size={14}
                strokeWidth={2.2}
              />
              {sector.label}
            </div>
          </motion.div>

          {/* Copy */}
          <div className="lg:col-span-6">
            <motion.div {...glide(0.05)}>
              <div className="flex items-center gap-3">
                <span
                  className="h-px w-12"
                  style={{ background: sector.color }}
                />
                <span
                  className="text-[10px] font-heading font-semibold uppercase tracking-widest"
                  style={{ color: sector.color }}
                >
                  {sector.tagline}
                </span>
              </div>
              <h2 className="mt-4 font-heading font-black text-3xl md:text-5xl leading-[1.02] tracking-tighter text-white">
                {sector.headline}
              </h2>
            </motion.div>

            <div className="mt-6 space-y-4">
              {sector.body.map((p, i) => (
                <motion.p
                  key={i}
                  {...glide(0.1 + i * 0.04)}
                  className="text-white/70 leading-relaxed text-base md:text-lg"
                >
                  {p}
                </motion.p>
              ))}
            </div>

            {/* Industry chips */}
            <motion.div
              {...glide(0.25)}
              className="mt-8 flex flex-wrap gap-2"
              data-testid={`sector-chips-${sector.key}`}
            >
              {sector.industries.map((ind) => (
                <span
                  key={ind.slug}
                  data-testid={`sector-chip-${ind.slug}`}
                  className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-medium"
                  style={{
                    background: "rgba(255,255,255,0.04)",
                    border: `1px solid ${sector.color}30`,
                    color: "rgba(255,255,255,0.85)",
                  }}
                >
                  {ind.label}
                </span>
              ))}
            </motion.div>

            {/* Buttons */}
            <motion.div
              {...glide(0.32)}
              className="mt-8 flex flex-wrap gap-3"
            >
              <Link
                to="/what-we-do"
                data-testid={`sector-view-services-${sector.key}`}
                className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-heading font-semibold text-white transition-all"
                style={{
                  background: sector.color,
                }}
              >
                View our services
                <ArrowUpRight size={14} />
              </Link>
              <Link
                to="/clients"
                className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-heading font-semibold text-white/80 border border-white/20 hover:border-white/40 hover:text-white transition-all"
              >
                See our clients
                <ArrowUpRight size={14} />
              </Link>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// Client logo orbit - subtle marquee of client names looping
// around the network. Kept restrained.
// ─────────────────────────────────────────────────────────────
function ClientOrbit() {
  const names = CLIENTS.slice(0, 40).map((c) => c.name);
  const doubled = [...names, ...names];
  return (
    <section
      data-testid="client-orbit"
      className="relative py-16 md:py-20 overflow-hidden border-y border-white/5"
    >
      <BlueprintBg opacity={0.04} />
      <div className="relative flex items-center gap-14 whitespace-nowrap animate-marquee">
        {doubled.map((name, i) => (
          <span
            key={i}
            className="font-heading font-bold text-white/25 text-sm md:text-base tracking-widest"
          >
            {name}
          </span>
        ))}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────
export default function Industries() {
  return (
    <main
      data-testid="industries-page"
      className="dark-section bg-slate-950 text-white"
      style={{ backgroundColor: "#070c17" }}
    >
      <Seo
        title="Industries we work with"
        description="One industry. Hundreds of specialisms. HighwaysIndustry.com connects design and engineering, construction and infrastructure, maintenance and operations, products and technology, and professional services - every part of the UK highways sector."
        path="/industries"
      />
      {/* ─────────── HERO ─────────── */}
      <section
        data-testid="industries-hero"
        className="relative min-h-[80vh] md:min-h-[92vh] flex items-end overflow-hidden"
      >
        <video
          className="absolute inset-0 w-full h-full object-cover opacity-45"
          autoPlay
          muted
          loop
          playsInline
          poster="/images/case-studies/hds-kier/hero.jpg"
        >
          <source src="/videos/hds-kier.mp4" type="video/mp4" />
        </video>
        <div
          aria-hidden
          className="absolute inset-0 bg-gradient-to-t from-[#070c17] via-[#070c17]/80 to-[#070c17]/40"
        />
        <BlueprintBg opacity={0.06} />

        <div className="relative mx-auto max-w-[1400px] w-full px-5 md:px-10 pb-12 sm:pb-16 md:pb-24 pt-28 sm:pt-32 md:pt-40 text-center">
          <motion.div
            {...glide(0, 0.35)}
            className="inline-flex items-center gap-3"
          >
            <span className="h-px w-12 bg-cyan-400" />
            <span className="text-[10px] font-heading font-semibold uppercase tracking-widest text-cyan-300">
              Industries we work with
            </span>
            <span className="h-px w-12 bg-cyan-400" />
          </motion.div>

          <motion.h1
            {...glide(0.08, 0.4)}
            data-testid="industries-headline"
            className="mt-6 font-heading font-black text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tighter mx-auto max-w-5xl"
          >
            One industry.
            <br />
            <span className="text-cyan-300 italic">Hundreds of specialisms.</span>
            <br />
            One platform connecting them all.
          </motion.h1>

          <motion.p
            {...glide(0.18, 0.4)}
            className="mt-8 text-white/70 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed"
          >
            Supporting businesses across every part of the UK&apos;s highways
            and infrastructure industry.
          </motion.p>

          {/* Animated glowing line travelling across */}
          <motion.div
            {...glide(0.28, 0.4)}
            className="mt-14 relative h-px w-full max-w-3xl mx-auto"
          >
            <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent" />
            <span
              className="absolute inset-y-[-1px] w-32 bg-gradient-to-r from-transparent via-cyan-300 to-transparent blur-[2px]"
              style={{
                animation: "hi-traverse 5s linear infinite",
              }}
            />
          </motion.div>
        </div>

        <style>{`
          @keyframes hi-traverse {
            0%   { transform: translateX(-100%); }
            100% { transform: translateX(300%); }
          }
        `}</style>
      </section>

      {/* ─────────── THE NETWORK ─────────── */}
      <section
        id="network"
        data-testid="network-section"
        className="relative py-16 sm:py-20 md:py-32 overflow-hidden scroll-mt-16"
      >
        <BlueprintBg opacity={0.08} />
        <div className="relative mx-auto max-w-[1400px] px-5 md:px-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-3">
              <span className="h-px w-12 bg-cyan-400" />
              <span className="text-[10px] font-heading font-semibold uppercase tracking-widest text-cyan-300">
                The Connected Industry
              </span>
              <span className="h-px w-12 bg-cyan-400" />
            </div>
            <h2 className="mt-6 font-heading font-black text-4xl md:text-6xl leading-[0.95] tracking-tighter">
              Every part of highways.
              <br />
              <span className="text-cyan-300 italic">Connected.</span>
            </h2>
            <p className="mt-6 text-white/65 text-base md:text-lg leading-relaxed">
              We work across five interconnected clusters that together make up
              the UK highways and infrastructure sector. Every project sits
              somewhere inside this network. So does every business we support.
            </p>
          </div>

          <div className="mt-16">
            <NetworkHub />
          </div>
        </div>
      </section>

      {/* ─────────── STATISTICS ─────────── */}
      <section
        data-testid="industries-stats"
        className="relative py-12 sm:py-16 md:py-24 border-y border-white/5"
      >
        <BlueprintBg opacity={0.04} />
        <div className="relative mx-auto max-w-[1400px] px-5 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { end: 2011, label: "Founded", format: false },
            { end: 1000, suffix: "+", label: "Industry companies" },
            { end: 5, suffix: "M+", label: "Annual industry reach" },
            { end: 25, suffix: "K+", label: "Monthly professionals" },
          ].map((s, i) => (
            <motion.div
              key={s.label}
              {...glide(i * 0.06, 0.4)}
              data-testid={`stat-${i}`}
              className="text-center md:text-left"
            >
              <div className="font-heading font-black text-4xl md:text-6xl tracking-tighter text-cyan-300">
                <Counter
                  end={s.end}
                  suffix={s.suffix || ""}
                  format={s.format !== false}
                />
              </div>
              <div className="mt-2 text-[11px] md:text-sm text-white/55 uppercase tracking-widest">
                {s.label}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ─────────── SECTOR CARDS ─────────── */}
      {SECTOR_NETWORK.map((sector, i) => (
        <SectorCard key={sector.key} sector={sector} index={i} />
      ))}

      {/* ─────────── CLIENT ORBIT ─────────── */}
      <ClientOrbit />

      {/* ─────────── FINAL CTA ─────────── */}
      <section
        data-testid="industries-final-cta"
        className="relative py-16 sm:py-24 md:py-36 overflow-hidden"
      >
        <BlueprintBg opacity={0.08} />
        {/* Converging network background */}
        <svg
          aria-hidden
          viewBox="0 0 1200 400"
          className="absolute inset-0 w-full h-full opacity-40"
          preserveAspectRatio="xMidYMid slice"
        >
          {SECTOR_NETWORK.map((s, i) => {
            const angle = (-90 + i * 72) * (Math.PI / 180);
            const x = 600 + 550 * Math.cos(angle);
            const y = 200 + 180 * Math.sin(angle);
            return (
              <motion.line
                key={s.key}
                x1={x}
                y1={y}
                x2={600}
                y2={200}
                stroke={s.color}
                strokeOpacity={0.5}
                strokeWidth={1}
                initial={{ pathLength: 0 }}
                whileInView={{ pathLength: 1 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.9,
                  delay: 0.1 + i * 0.08,
                  ease: EASE,
                }}
              />
            );
          })}
          <circle cx={600} cy={200} r={4} fill="#22D3EE">
            <animate
              attributeName="r"
              values="4;10;4"
              dur="2.4s"
              repeatCount="indefinite"
            />
            <animate
              attributeName="opacity"
              values="1;0.4;1"
              dur="2.4s"
              repeatCount="indefinite"
            />
          </circle>
        </svg>

        <div className="relative mx-auto max-w-[1400px] px-5 md:px-10 text-center">
          <motion.div
            {...glide(0, 0.4)}
            className="inline-flex items-center gap-3"
          >
            <span className="h-px w-12 bg-cyan-400" />
            <span className="text-[10px] font-heading font-semibold uppercase tracking-widest text-cyan-300">
              The network reforms
            </span>
            <span className="h-px w-12 bg-cyan-400" />
          </motion.div>

          <motion.h2
            {...glide(0.08, 0.4)}
            className="mt-6 font-heading font-black text-4xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tighter max-w-4xl mx-auto"
          >
            Whatever your sector,
            <br />
            <span className="text-cyan-300 italic">
              we&apos;re here to help you connect.
            </span>
          </motion.h2>
          <motion.p
            {...glide(0.16, 0.4)}
            className="mt-6 text-white/65 text-lg max-w-2xl mx-auto leading-relaxed"
          >
            HighwaysIndustry.com brings every part of the sector together, from
            engineering and construction through to maintenance, technology and
            the professionals who make it all possible.
          </motion.p>

          <motion.div
            {...glide(0.24, 0.4)}
            className="mt-10 flex flex-col sm:flex-row gap-3 justify-center"
          >
            <Link
              to="/what-we-do"
              data-testid="cta-explore-services"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-cyan-400 text-slate-950 font-heading font-semibold hover:bg-cyan-300 transition-colors text-sm"
            >
              Explore services
              <ArrowUpRight size={14} />
            </Link>
            <a
              href="https://highwaysindustry.com/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-visit-media-platform"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full border border-white/25 text-white font-heading font-semibold hover:border-cyan-300 hover:text-cyan-300 transition-colors text-sm"
            >
              Read HighwaysIndustry.com
              <ArrowUpRight size={14} />
            </a>
            <Link
              to="/contact"
              data-testid="cta-contact"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full border border-white/25 text-white font-heading font-semibold hover:border-cyan-300 hover:text-cyan-300 transition-colors text-sm"
            >
              Contact us
              <ArrowUpRight size={14} />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
